import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconCircleMinus, IconPlus, IconSearch } from '@tabler/icons-react';
import { useShellProfile } from '../../state/useShellProfile';
import { AppDialogFrame } from '../../components/overlays/AppDialogFrame';
import { SupportErrorMessage } from '../../components/overlays/EngineeringSupportBanner';
import { readDomainRouterErrorMessage } from '../../state/domainRouterError';
import {
  useGetApplicantApplicationsQuery,
  useUpdateAdmissionsApplicationStatusMutation,
} from '../../state/domainRouterApi';

const Empty = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm text-irongrey/50 font-medium">{children}</p>
);

const ErrorState = ({ children }: { children: React.ReactNode }) => (
  <SupportErrorMessage>{children}</SupportErrorMessage>
);

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const readText = (value: unknown): string => String(value ?? '').trim();

const PARENT_APPLICATIONS_LIST_LIMIT = 100;

const toEpochMilliseconds = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value < 1_000_000_000_000 ? value * 1000 : value;
  }

  const text = readText(value);
  if (!text) return null;

  const numeric = Number(text);
  if (Number.isFinite(numeric) && numeric > 0) {
    return numeric < 1_000_000_000_000 ? numeric * 1000 : numeric;
  }

  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? parsed : null;
};

const readApplicationSortTimestamp = (application: Record<string, unknown>): number => (
  toEpochMilliseconds(application.submittedAt)
    ?? toEpochMilliseconds(application.updatedAt)
    ?? toEpochMilliseconds(application.createdAt)
    ?? 0
);

const sortNewestApplicationsFirst = (
  applications: Record<string, unknown>[],
): Record<string, unknown>[] => (
  [...applications].sort((left, right) => (
    readApplicationSortTimestamp(right) - readApplicationSortTimestamp(left)
  ))
);

const toStatusLabel = (value: string): string => value.replace(/_/g, ' ');

const toStatusToken = (value: string): string => value.toLowerCase().replace(/_/g, '-');

const statusBadgeClass = (status: string): string => {
  const token = toStatusToken(status);
  if (token === 'accepted' || token === 'enrolled' || token === 'accepted-pending-enrollment') {
    return 'bg-jungle/10 text-jungle';
  }
  if (token === 'submitted' || token === 'under-review' || token === 'decision-pending' || token === 'interview-scheduled') {
    return 'bg-primary/10 text-primary';
  }
  if (token === 'waitlisted' || token === 'acceptance-expired') {
    return 'bg-amber-100 text-amber-700';
  }
  if (token === 'denied' || token === 'withdrawn') {
    return 'bg-strawberry/10 text-strawberry';
  }
  return 'bg-gray-100 text-irongrey/60';
};

const NON_WITHDRAWABLE_STATUS_TOKENS = new Set([
  'withdrawn',
  'denied',
  'enrolled',
  'acceptance-expired',
]);

const canWithdrawApplicationStatus = (status: string): boolean => {
  const token = toStatusToken(status || 'submitted');
  return !NON_WITHDRAWABLE_STATUS_TOKENS.has(token);
};

const getApplicationActionErrorMessage = (error: unknown): string => {
  const domainMessage = readDomainRouterErrorMessage(error, '');
  if (domainMessage) return domainMessage;
  if (error instanceof Error && error.message.trim()) return error.message.trim();
  return 'Unable to withdraw this application right now.';
};

type ApplicationActionTarget = {
  id: string;
  code: string;
  familyName: string;
  studentName: string;
};

type ApplicationFeedback = {
  tone: 'success' | 'error';
  message: string;
};

const familyInitials = (name: string): string => {
  const cleaned = name
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 2);
  if (!cleaned.length) return 'FA';
  if (cleaned.length === 1) return cleaned[0].slice(0, 2).toUpperCase();
  return `${cleaned[0][0]}${cleaned[1][0]}`.toUpperCase();
};

const readStudentName = (application: Record<string, unknown>): string => {
  const children = Array.isArray(application.children) ? application.children : [];
  const firstChild = asRecord(children[0]);
  const childInfo = asRecord(firstChild.childInfo);
  const firstName = readText(childInfo.firstName);
  const lastName = readText(childInfo.lastName);
  return `${firstName} ${lastName}`.trim();
};

type ApplicationsHeaderProps = {
  beginApplicationPath: string;
};

const ApplicationsHeader = ({ beginApplicationPath }: ApplicationsHeaderProps) => (
  <header className="flex items-center justify-between gap-4 flex-wrap">
    <Link
      to={beginApplicationPath}
      className="inline-flex w-full items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-jungle text-white font-bold text-sm hover:opacity-90 transition-opacity md:w-auto"
      data-testid="landing-start-application"
      data-test-id="landing-start-application"
    >
      <IconPlus size={18} />
      Begin Application
    </Link>
  </header>
);

type ApplicationRowProps = {
  application: Record<string, unknown>;
  withdrawalInFlight: boolean;
  onRequestWithdraw: (target: ApplicationActionTarget) => void;
};

const ApplicationRow = ({ application, withdrawalInFlight, onRequestWithdraw }: ApplicationRowProps) => {
  const familyName = readText(asRecord(application.applicant).familyName) || readText(application.familyName) || 'Name required';
  const studentName = readStudentName(application);
  const academicYear = readText(application.academicYear);
  const status = readText(application.status) || 'submitted';
  const code = readText(application.code);
  const applicationId = readText(application.id);
  const canWithdraw = Boolean(applicationId) && canWithdrawApplicationStatus(status);

  return (
    <tr
      data-testid="parent-portal-application-row"
      className="group border-t border-gray-100 transition-colors hover:bg-gray-50/80 focus-within:bg-gray-50/80"
      aria-label={`Application ${code || familyName}`}
    >
      <td className="px-6 py-4 align-middle min-w-[240px]">
        <div className="block rounded-lg">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 rounded-full bg-primary/10 text-primary text-xs font-bold inline-flex items-center justify-center shrink-0">
              {familyInitials(familyName)}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-bold text-irongrey truncate group-hover:text-primary">{familyName}</p>
              <p className="text-xs text-irongrey/50 font-medium truncate">{studentName || 'Student not set'}</p>
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 text-sm font-semibold text-irongrey/70 align-middle min-w-[150px]">
        <div className="block rounded-lg">
          {academicYear || 'Year pending'}
        </div>
      </td>
      <td className="px-6 py-4 align-middle min-w-[160px]">
        <div className="block rounded-lg">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide ${statusBadgeClass(status)}`}>
            {toStatusLabel(status)}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 text-right text-sm font-bold text-irongrey align-middle min-w-[170px]">
        <div className="block rounded-lg">
          {code || 'Code pending'}
        </div>
      </td>
      <td className="px-6 py-4 text-right align-middle min-w-[180px]">
        {canWithdraw ? (
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-strawberry/20 bg-strawberry/5 px-3 py-2 text-xs font-bold text-strawberry transition-colors hover:bg-strawberry/10 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => onRequestWithdraw({
              id: applicationId,
              code,
              familyName,
              studentName,
            })}
            disabled={withdrawalInFlight}
            data-testid="parent-portal-application-withdraw-button"
          >
            <IconCircleMinus size={15} />
            Withdraw
          </button>
        ) : (
          <span className="text-xs font-semibold text-irongrey/40" data-testid="parent-portal-application-no-action">
            No action
          </span>
        )}
      </td>
    </tr>
  );
};

export function PortalApplicationsPage() {
  const { profile } = useShellProfile();
  const applicantId = profile?.userSub ?? null;
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pendingWithdrawal, setPendingWithdrawal] = useState<ApplicationActionTarget | null>(null);
  const [feedback, setFeedback] = useState<ApplicationFeedback | null>(null);
  const [withdrawApplication, withdrawApplicationState] = useUpdateAdmissionsApplicationStatusMutation();

  const applicationsQuery = useGetApplicantApplicationsQuery(
    { applicantId: applicantId ?? '', limit: PARENT_APPLICATIONS_LIST_LIMIT },
    { skip: !applicantId },
  );

  const items = applicationsQuery.data?.items ?? [];
  const records = useMemo(
    () => sortNewestApplicationsFirst(items.map((item) => asRecord(item))),
    [items],
  );

  const statusOptions = useMemo(() => {
    const unique = new Set<string>();
    records.forEach((record) => {
      const status = readText(record.status);
      if (status) unique.add(status);
    });
    return ['all', ...Array.from(unique).sort((a, b) => a.localeCompare(b))];
  }, [records]);

  const filteredRecords = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    return records.filter((record) => {
      const familyName = readText(asRecord(record.applicant).familyName) || readText(record.familyName);
      const studentName = readStudentName(record);
      const academicYear = readText(record.academicYear);
      const status = readText(record.status) || 'submitted';
      const code = readText(record.code);
      const searchCorpus = `${familyName} ${studentName} ${academicYear} ${status} ${code}`.toLowerCase();

      const searchMatch = query.length === 0 || searchCorpus.includes(query);
      const statusMatch = statusFilter === 'all' || status === statusFilter;
      return searchMatch && statusMatch;
    });
  }, [records, searchValue, statusFilter]);


  const closeWithdrawalDialog = () => {
    if (withdrawApplicationState.isLoading) return;
    setPendingWithdrawal(null);
  };

  const handleConfirmWithdrawal = async () => {
    if (!pendingWithdrawal) return;
    setFeedback(null);
    try {
      await withdrawApplication({
        applicationId: pendingWithdrawal.id,
        status: 'withdrawn',
      }).unwrap();
      setPendingWithdrawal(null);
      setFeedback({
        tone: 'success',
        message: `Application ${pendingWithdrawal.code || pendingWithdrawal.familyName} was withdrawn.`,
      });
      await applicationsQuery.refetch?.();
    } catch (error) {
      setFeedback({
        tone: 'error',
        message: getApplicationActionErrorMessage(error),
      });
    }
  };

  return (
    <main
      data-testid="parent-portal-applications"
      data-extracted-from="owned:ystnj/parent-portal/applications"
      className="w-full space-y-5"
    >
      <ApplicationsHeader beginApplicationPath="/parent/portal--new/applications" />

      <section className="bg-white border border-gray-100 rounded-2xl shadow-sm p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5 flex items-center flex-1 min-w-0">
          <IconSearch size={16} className="text-irongrey/40 mr-2 shrink-0" />
          <input
            type="text"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            className="bg-transparent border-none outline-none text-sm font-medium text-irongrey placeholder:text-irongrey/40 w-full min-w-0"
            placeholder="Search student, application code, or status..."
            data-testid="parent-portal-applications-search"
          />
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <label htmlFor="parent-applications-status-filter" className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest whitespace-nowrap">
            Status
          </label>
          <select
            id="parent-applications-status-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5 text-sm font-semibold text-irongrey min-w-[170px]"
            data-testid="parent-portal-applications-status-filter"
          >
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Statuses' : toStatusLabel(status)}
              </option>
            ))}
          </select>
        </div>

        <div className="text-xs font-semibold text-irongrey/50 md:ml-auto">
          Showing {filteredRecords.length} of {records.length} applications
        </div>
      </section>

      {!applicantId ? <ErrorState>Unable to resolve your account identity.</ErrorState> : null}
      {feedback?.tone === 'error' ? (
        <SupportErrorMessage
          className="rounded-2xl px-4 py-3 text-sm"
          data-testid="parent-portal-applications-feedback"
        >
          {feedback.message}
        </SupportErrorMessage>
      ) : feedback ? (
        <div
          role="status"
          className="rounded-2xl border border-jungle/20 bg-jungle/5 px-4 py-3 text-sm font-semibold text-jungle"
          data-testid="parent-portal-applications-feedback"
        >
          {feedback.message}
        </div>
      ) : null}

      {applicationsQuery.isLoading ? <Empty>Loading applications...</Empty> : null}
      {applicationsQuery.error ? <ErrorState>Unable to load applications right now.</ErrorState> : null}

      <section className="bg-white border border-gray-100 rounded-3xl shadow-card overflow-hidden" aria-label="Submitted applications">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse">
            <caption className="sr-only">Submitted applications</caption>
            <thead>
              <tr className="bg-gray-50/70 text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">
                <th className="px-6 py-4 text-left">Family</th>
                <th className="px-6 py-4 text-left">Academic Year</th>
                <th className="px-6 py-4 text-left">Status</th>
                <th className="px-6 py-4 text-right">Code</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!applicationsQuery.isLoading && !applicationsQuery.error && records.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8">
                    <Empty>No applications have been submitted for this account.</Empty>
                  </td>
                </tr>
              ) : null}

              {!applicationsQuery.isLoading && !applicationsQuery.error && records.length > 0 && filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8">
                    <Empty>No applications match the current search and status filters.</Empty>
                  </td>
                </tr>
              ) : null}

              {filteredRecords.map((record, index) => {
                const id = readText(record.id) || readText(record.code) || `application-${index}`;
                return (
                  <ApplicationRow
                    key={id}
                    application={record}
                    withdrawalInFlight={withdrawApplicationState.isLoading}
                    onRequestWithdraw={(target) => {
                      setFeedback(null);
                      setPendingWithdrawal(target);
                    }}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <AppDialogFrame
        open={Boolean(pendingWithdrawal)}
        onClose={closeWithdrawalDialog}
        title="Withdraw application"
        description="This keeps the application record for audit history but removes it from active admissions review."
        testId="parent-portal-withdraw-application-dialog"
        footer={(
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-irongrey transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={closeWithdrawalDialog}
              disabled={withdrawApplicationState.isLoading}
              data-testid="parent-portal-application-withdraw-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-xl bg-strawberry px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => void handleConfirmWithdrawal()}
              disabled={withdrawApplicationState.isLoading}
              data-testid="parent-portal-application-withdraw-confirm"
            >
              {withdrawApplicationState.isLoading ? 'Withdrawing...' : 'Withdraw application'}
            </button>
          </div>
        )}
      >
        <p className="text-sm font-medium leading-6 text-irongrey/70">
          {pendingWithdrawal
            ? `Withdraw ${pendingWithdrawal.studentName || pendingWithdrawal.familyName || 'this application'}${pendingWithdrawal.code ? ` (${pendingWithdrawal.code})` : ''}?`
            : 'Withdraw this application?'}
        </p>
      </AppDialogFrame>
    </main>
  );
}
